import { Grid, Typography, Button, Divider, IconButton, Link } from '@mui/material'
import moment from 'moment'
import { IJobByStatus } from '../../../Types/Job'
import { useNavigate } from 'react-router-dom'
import { JobDeletionModal } from './JobDeletionModal'
import { useState } from 'react'
import { Delete, Edit } from '@mui/icons-material'
import { useUser } from '../../../Hooks/useUser'
import { KaziButton } from '../../../Shared Components/Kazi/KaziButton'
import { useKaziNavigate } from '../../../Hooks/useKaziNavigate'
import { useGetJobQuery } from '../../../Redux/api/jobApi'
import { preloadJobPosting, setJobPostingModalOpen } from '../../../Redux/slices/JobPostingCreationSlice'
import { useAppDispatch } from '../../../Redux/store'
import { 
    setTitle, 
    setLevel, 
    setTags, 
    setDescription, 
    setResponsibilities, 
    setSkills, 
    setPayType, 
    setPayAmount, 
    setPayAmountMax, 
    setJobType, 
    setPayRate, 
    setDuration, 
    setDurationType, 
    setPrimaryLanguage, 
    setQuestions, 
    setWorkSite, 
    setSpecialization, 
    setActiveStep,
    setEditingJobLocator,
    setCreationMethod,
    setDeliverables
} from '../../../Redux/slices/JobPostCreationSlice'

interface JobViewProps {
    job: IJobByStatus
}

export const JobView = ({ job }:JobViewProps) => {
    /* ******************** Hooks ******************** */
    const { navigateWithBack } = useKaziNavigate();
    const { user } = useUser();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    /* ******************** Variables ******************** */
    const [modalOpen, setModalOpen] = useState(false);
    const isNonActive = job?.status === 'open' || job?.status === 'draft';
    const isDraft = job?.status === 'draft';
    const showIcons = isNonActive; // Show icons only for non-active jobs (open or draft)
    // @ts-ignore
    const talentInvites = user?.talent_invites?.filter( invite => invite.job_locator === job?.locator) ?? []
    const showReviewButton = job?.applicants.length > 0 || talentInvites.length > 0
    
    // Get full job data for drafts
    const { data: fullJobData } = useGetJobQuery(job.locator, {
        skip: !isDraft
    });
    
    /* ******************** Functions ******************** */
    const handleDelete = () => {
        setModalOpen(true);
    }

    const handleEdit = () => {
        if (isDraft && fullJobData) {
            // For all drafts (both AI and manual), navigate to JobPostCreation flow at review step (step 3)
            // Prepopulate the form with the draft data
            dispatch(setTitle(fullJobData.title || ''));
            dispatch(setLevel(fullJobData.level || 'flexible'));
            dispatch(setTags(fullJobData.tags || []));
            dispatch(setDescription(fullJobData.description || ''));
            dispatch(setResponsibilities(fullJobData.responsibilities || []));
            dispatch(setSkills(fullJobData.skills?.map(s => s.skill) || []));
            dispatch(setPayType(fullJobData.pay_type || 'Fixed'));
            dispatch(setPayAmount(fullJobData.pay_amount || ''));
            dispatch(setPayAmountMax(fullJobData.pay_amount_max || ''));
            dispatch(setJobType(fullJobData.type || 'Short-term'));
            dispatch(setPayRate(fullJobData.pay_rate || 'One-time'));
            dispatch(setDuration(fullJobData.duration?.toString() || ''));
            dispatch(setDurationType(fullJobData.duration_type || ''));
            dispatch(setPrimaryLanguage(fullJobData.primary_language || ''));
            dispatch(setQuestions(fullJobData.questions?.map(q => q.question) || []));
            dispatch(setWorkSite(fullJobData.work_site || 'remote'));
            dispatch(setSpecialization(fullJobData.specialization || ''));
            dispatch(setActiveStep(3)); // Set to review step
            dispatch(setEditingJobLocator(job.locator));
            dispatch(setCreationMethod(fullJobData.creation_method || 'manual'));
            dispatch(setDeliverables(fullJobData.deliverables || []));
            navigate('/post-creation');
        } else {
            navigate(`/job-postings/posting/${job?.locator}/edit`);
        }
    }
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    return (
        <>
            <Grid xs gap={2} display={'flex'} flexDirection={'column'} marginBottom={2}>
                <Grid xs display={'flex'} flexDirection={'row'}>
                    <Grid  flex={1} display={'flex'} flexDirection={'row'}>
                        <Grid  display={'flex'} flexDirection={'column'}>
                            <Link href={`/job-postings/posting/${job?.locator}`}><Typography variant='body-medium'>{job?.title}</Typography></Link>
                            <Typography variant='body-smaller'color={'black.300'}>Posted on {moment(job?.posting_date).format('MMM DD, YYYY')}</Typography>
                        </Grid>
                    </Grid>
                    <Grid flex={1} justifyContent={'flex-end'} display={'flex'} flexDirection={'row'} gap={'10px'} alignItems={'center'}>
                        <Grid display={'flex'} flexDirection={'row'} gap={'16px'} alignItems={'center'}>
                            {!isDraft && (
                                <>
                                    <Typography variant='body-small' >{talentInvites.length ?? 0} invites</Typography>
                                    <Typography variant='body-small' >{job?.applicants.length} applicants</Typography>                            
                                    <Button 
                                        variant='text' 
                                        disabled={!showReviewButton}
                                        sx={{ 
                                            fontWeight: 'bold', 
                                            opacity: `${ showReviewButton ? 100 : 0}` 
                                        }} 
                                        onClick={() => navigateWithBack(`/job-postings/applications/${job?.locator}`)}
                                    >
                                        Review
                                    </Button>
                                </>
                            )}
                        </Grid>
                        <IconButton sx={{ marginRight: 2, opacity: showIcons ? 100 : 0}} onClick={handleDelete}>
                            <Delete />
                        </IconButton>
                        <IconButton 
                                sx={{ 
                                    opacity: `${ showIcons ? 100 : 0}` 
                                }} 
                            onClick={handleEdit}
                        >
                            <Edit />
                        </IconButton>
                    </Grid>
                </Grid> 
                <Divider />
            </Grid>
            <JobDeletionModal 
                open={modalOpen}
                setOpen={setModalOpen}
                locator={job?.locator}
            />
        </>
    )
}